import { useCurrentPortfolioStore } from '../stores/currentPortfolioStore'
import { useTranslationsStore } from '../stores/translationsStore'

export type UnitSystem = 'imperial' | 'metric'

type FormatNumberProps = {
    number: number
    unitSystem?: UnitSystem
    defaultValue?: number
    precision?: number
}

type ScaleUnit = {
    scale: number
    label: string
}

export function useFormatting() {
    const { translate } = useTranslationsStore()
    const currentPortfolioStore = useCurrentPortfolioStore()

    /**
     * Formats a number according to the unit system.
     * @param props.number - The number to format.
     * @param props.unitSystem - The unit system to use. By default, metric is used.
     * @param props.defaultValue - The default value to return if the number is null or undefined. By default, 0 is used.
     * @returns The formatted number.
     */
    function formatNumber(props: FormatNumberProps): string {
        const _unitSystem = props.unitSystem ?? currentPortfolioStore.data?.default_unit
        const isImperial = _unitSystem === 'imperial'
        // Use 'en-US' for imperial (comma as thousands separator, dot as decimal)
        // Use 'de-DE' for metric (dot as thousands separator, comma as decimal)
        const locale = isImperial ? 'en-US' : 'de-DE'
        const formatter = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: props.precision ?? 2,
        })
        return formatter.format(props.number)
    }

    /**
     * Formats large numbers with appropriate scaling (K, M, B, etc.).
     * Can be used for any large values: currency, population, metrics, etc.
     * @param number - The number to format (number or string).
     * @returns Object with formatted value and scale label.
     */
    function formatLargeNumber(number: string | number = 0): { value: number, scale: string } {
        const value = Number(number)

        const units: ScaleUnit[] = [
            { scale: 1000000000, label: 'B' },
            { scale: 1000000, label: 'M' },
            { scale: 1000, label: 'K' },
        ]

        for (const unit of units) {
            if (value >= unit.scale) {
                const scaledValue = Math.ceil(value / unit.scale * 1000) / 1000
                return { value: scaledValue, scale: unit.label }
            }
        }

        return { value, scale: '' }
    }

    /**
     * Automatically converts between kWh and MWh based on the magnitude of the number.
     * Large kWh values are converted to MWh, small MWh values are converted to kWh.
     * @param value - The energy value to format.
     * @param unit - The current unit ('kwh' or 'mwh').
     * @returns Object with the converted value and appropriate unit.
     */
    function formatEnergyUnit(value: number, unit: 'kwh' | 'mwh'): { value: number, unit: string } {
        const KWH_TO_MWH_THRESHOLD = 1000 // Convert kWh to MWh when >= 1000 kWh
        const MWH_TO_KWH_THRESHOLD = 1 // Convert MWh to kWh when < 1 MWh

        const labels = {
            kwh: translate('general.units.kwh') || 'kWh',
            mwh: translate('general.units.mwh') || 'MWh',
        }

        if (unit === 'kwh') {
            if (value >= KWH_TO_MWH_THRESHOLD) {
                return { value: value / 1000, unit: labels.mwh }
            }
            return { value, unit: labels.kwh }
        }

        if (unit === 'mwh') {
            if (value < MWH_TO_KWH_THRESHOLD) {
                return { value: value * 1000, unit: labels.kwh }
            }
            return { value, unit: labels.mwh }
        }

        return { value, unit: labels[unit] }
    }

    return { formatNumber, formatLargeNumber, formatEnergyUnit }
}
